export const paths = {
  main: '/',
  // NOTE: `cockpit` lives in a SEPARATE React tree mounted at module load
  // in App.tsx. Do NOT use this value with React Router's `<Link>` or
  // `navigate()` — those stay inside the internal app's router and will
  // hit the catch-all NotFound route. Use `window.location.assign(paths.cockpit)`
  // (or a plain `<a href={paths.cockpit}>`) for in-app links into the cockpit
  // so the bundle reloads and the cockpit tree mounts.
  cockpit: '/cockpit',
  // BID's own Pipedrive-backed deal funnel. An ordinary page of THIS tree --
  // no `external`, no hard navigation. It shipped at `/cockpit/deals` and
  // moved here once the cockpit became the portfolio company's product rather
  // than a shared surface; `/cockpit/deals` still resolves, as a redirect to
  // this path (see pages/Cockpit/CockpitApp.tsx).
  crmDealFunnel: '/crm/deal-funnel',
  // The counterparty directory -- advisors, law firms, banks and consultants
  // we transact through, with the deals each is recorded as having advised.
  // A page of THIS tree, gated by the `cortex.counterparties` section (unlike
  // the deal funnel above, which has no section key to be guarded by).
  crmCounterparties: '/crm/counterparties',
  // The relationship tiers (label, criteria, outreach interval, counts). A
  // static segment, so it wins over the `:id` record route below it.
  crmCounterpartyTiers: '/crm/counterparties/tiers',
  // The filter state travels in the query string, so a filtered list is a
  // link and Back out of a record restores the list the user left.
  crmCounterpartyDetail: '/crm/counterparties/:id',
  search: '/search',
  pageSearch: '/page-search',
  // The assistant's full-screen home. The floating SupportChatWidget is
  // suppressed here (RootLayot) so only one assistant is ever on screen.
  chat: '/chat',
  validation: '/validation',
  crawlerInbox: '/crawler-inbox',
  statistics: '/statistics',
  crawlerOps: {
    main: '/crawler-ops',
    pipedrive: '/crawler-ops/pipedrive',
    mlStats: '/crawler-ops/ml-stats',
    signalTracking: '/crawler-ops/signal-tracking',
    userActivity: '/crawler-ops/user-activity',
    dashboard: '/crawler-ops/dashboard',
    enrichmentPerformance: '/crawler-ops/enrichment-performance',
    swClassifier: '/crawler-ops/sw-classifier',
    crawlQueue: '/crawler-ops/crawl-queue',
  },
  benchmarking: '/benchmarking',
  aiUpload: '/ai-upload',
  notifications: '/notifications',
  pipedriveActivity: '/notifications/pipedrive-activity',
  // Gated by the `todos_enabled` feature toggle (project/shared/feature_toggles.py),
  // not by a section key -- every todo op's server-side guard
  // (`_require_todo_access`, project/gql_schema/todo_ops.py) checks the toggle
  // and internal-user status only, so there is no section grant for
  // SectionGuard to check here either.
  todos: '/todos',
  settings: '/settings',
  dealAssessmentTemplate: '/settings/deal-assessment',
  aiThreatDesign: '/settings/ai-threat-design',
  scheduledTasks: '/settings/scheduled-tasks',
  appInsight: '/settings/app-insight',
  meetingTemplates: '/settings/meeting-templates',
  featureToggles: '/settings/feature-toggles',
  fileConventionRules: '/settings/file-convention-rules',
  dealLostReasons: '/settings/deal-lost-reasons',
  dealStages: '/settings/deal-stages',
  staffSyncStatus: '/settings/staff-sync-status',
  // Curate what Cortex has indexed from and shared out of the caller's own
  // mailbox (envelope/subject indexing + cortex-shared full-text sharing).
  // Backed by project/web/email_timeline_routes.py's /me/* endpoints.
  emailSync: '/settings/email-sync',
  // Incoming and outgoing email-access requests -- the same /me/requests*
  // endpoints' management surface, kept as a separate route from emailSync
  // above (a different slice of the same email_timeline_routes.py blueprint).
  emailAccess: '/settings/email-access',

  evaluation: {
    evaluation: '/evaluation',
    company: '/evaluation/company',
    market: '/evaluation/market',
    execution: '/evaluation/execution',
    // Legacy path from before the section was renamed (2026-08-25
    // direction) — kept only for the redirect route.
    dueDiligence: '/evaluation/due-diligence',
    customerSegment: '/customer-segment',
    // The canonical "open this company" link now lands on the tabbed workspace
    // (Overview tab). Every navigate(`${paths.evaluation.dfm}?companyId=…`) call
    // site is rewired through this one constant.
    dfm: '/evaluation/company',
    // Legacy standalone DFM page — kept routable for old bookmarks and the
    // Chrome-extension fallback.
    dfmStandalone: '/evaluation/dfm',
    techAssessment: '/evaluation/tech-assessment',
    companyFiles: '/evaluation/company-files',
    companyMeetings: '/evaluation/company-meetings',
    // Gated by the `todos_enabled` feature toggle, same as `todos` above --
    // `companyTodos`/`createTodo` (project/gql_schema/todo_ops.py) enforce it
    // resolver-side regardless of what this route renders.
    companyTodos: '/evaluation/company-todos',
    marketResearch: '/evaluation/market-research',
    aiThreatIndex: '/evaluation/ai-threat-index',
    companyAnnotations: '/evaluation/company-annotations',
    marketSegments: '/evaluation/market-segments',
    bbLists: '/evaluation/bb-lists',
    // Redesigned buy-and-build add-on sourcing screen. Gated by the
    // cortex_config `dealScoutEnabled` flag (off by default); coexists with
    // bbLists during rollout.
    dealScout: '/evaluation/deal-scout',
    startups: '/evaluation/startups',
    outreach: '/evaluation/outreach',
    // Nested under `outreach` because it feeds that page's renderer, not a
    // sibling concern: the manifest a user writes against here is the same
    // one `scripts/ingest_captured_handwriting.py` reads to build the glyph
    // library the Mailing tab's letters draw from. No backend persistence in
    // this slice -- capture, then export/import JSON by hand.
    handwriting: '/evaluation/outreach/handwriting',
  },
  companyFinder: {
    softwareCategories: 'software-categories',
    customerIndustries: 'customer-industries',
    matrixSearch: 'matrix-search',
  },
  research: {
    valuationModel: '/research/valuation-model',
    industryBenchmarks: '/research/industry-benchmarks',
    industryCodePilot: '/research/industry-code-pilot',
  },
  dataQuality: {
    main: '/data-quality',
    review: '/data-quality/:queueCode',
  },
  dealAnnouncements: '/deal-announcements',
  transactions: '/transactions',
  transactionsInvestors: '/transactions/investors',
  industryBenchmarks: '/industry-benchmarks',
  itSupport: '/it-support',
  validationV2: '/validation-v2',
  investorsGraph: '/investors-graph',
  survey: '/survey/:meetingId',
  gdriveRelink: '/companies/:companyId/gdrive-relink/:requestId',
  meetings: '/meetings',
  // Origination timesheet: log your own time. Guarded by `cortex.timesheet`
  // (project/services/access/sections.py), which is in
  // STAFF_EXCLUDED_SECTIONS -- being BID staff is not enough, an explicit
  // grant is required (ADR-0083). The reporting surface at
  // `/timesheet/report` answers to a *different* section key
  // (`cortex.timesheet_reporting`) and is its own page rather than a tab of
  // this one, because the two authorities are separate: reaching your own
  // timesheet must not mean reaching a colleague's.
  timesheet: '/timesheet',
  timesheetReport: '/timesheet/report',
  admin: {
    questionnaires: '/admin/questionnaires',
    dataEditor: '/admin/data-editor',
    sellerPortal: '/admin/seller-portal',
    vdr: '/admin/vdr',
  },
  organization: {
    members: '/organization/members',
  },
  platformAdmin: '/platform-admin',
  platformAdminUsers: '/platform-admin/users',
  platformAdminUsersManagement: '/platform-admin/users-management',
  platformAdminCockpitTeams: '/platform-admin/cockpit-teams',
  platformAdminClaudeQuotaRequests: '/platform-admin/claude-quota-requests',
};
